import { useMutation, useQueryClient } from '@tanstack/react-query';
import { CircleCheck, KeyRound, MailCheck, TriangleAlert } from 'lucide-react';
import { useEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import { Logo } from '../components/Layout';
import { Alert, Button, Field, Input, Spinner } from '../components/ui';
import { api, errorMessage } from '../lib/api';
import { useAuth } from '../lib/auth';
import { openSchool } from '../lib/school';

interface LinkResult {
  ok: true;
  email: string;
  school: string | null;
}

/** Where to sign in afterwards: the school's own page, or Edvora's. */
const signInHref = (school?: string | null) => (school ? `/${school}/login` : '/login');

function AccountCard({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 px-5 py-12">
      <div className="w-full max-w-md">
        <a href="/login" className="mb-8 flex items-center justify-center gap-3">
          <Logo />
          <div className="leading-tight">
            <p className="font-semibold text-slate-900">Edvora</p>
            <p className="text-[11px] font-medium tracking-wider text-brand-700 uppercase">Campus Asset</p>
          </div>
        </a>
        <div className="rounded-2xl border border-slate-200 bg-white p-8 shadow-sm">
          <div className="mb-4 flex size-11 items-center justify-center rounded-full bg-brand-50 text-brand-700 [&_svg]:size-5">{icon}</div>
          <h1 className="text-xl font-semibold tracking-tight text-balance text-slate-900">{title}</h1>
          <div className="mt-3 space-y-4 text-sm text-slate-600">{children}</div>
        </div>
      </div>
    </div>
  );
}

/** /verify-email?token=…: the link in the email that confirms an account's address. */
export function VerifyEmailPage() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const { me } = useAuth();
  const queryClient = useQueryClient();
  const started = useRef(false);
  const verify = useMutation({
    mutationFn: () => api.post<LinkResult>('/auth/verify-email', { token }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['me'] }),
  });
  const resend = useMutation({ mutationFn: () => api.post<{ ok: true; email: string }>('/auth/verify-email/resend') });

  useEffect(() => {
    // Once, even when React runs effects twice in development.
    if (!token || started.current) return;
    started.current = true;
    verify.mutate();
  }, [token, verify]);

  if (!token) {
    return (
      <AccountCard icon={<TriangleAlert />} title="This link is incomplete">
        <p>Open the link from your email again. If your email app split it across lines, copy the whole address into your browser.</p>
      </AccountCard>
    );
  }
  if (verify.isPending || verify.isIdle) {
    return (
      <AccountCard icon={<MailCheck />} title="Confirming your email…">
        <Spinner className="size-5" />
      </AccountCard>
    );
  }
  if (verify.isSuccess) {
    const { email, school } = verify.data;
    return (
      <AccountCard icon={<CircleCheck />} title="Your email is confirmed">
        <p>
          <strong className="text-slate-900">{email}</strong> is confirmed for your Edvora account. We’ll send trial and billing updates there.
        </p>
        {school ? (
          <Button variant="primary" className="w-full" onClick={() => openSchool(school)}>
            Open your school
          </Button>
        ) : (
          <Link to="/login" className="block">
            <Button variant="primary" className="w-full">
              Sign in
            </Button>
          </Link>
        )}
      </AccountCard>
    );
  }
  return (
    <AccountCard icon={<TriangleAlert />} title="This link didn’t work">
      <p>{errorMessage(verify.error)}</p>
      {me ? (
        resend.isSuccess ? (
          <Alert tone="green">We sent a new link to {resend.data.email}.</Alert>
        ) : (
          <>
            {resend.error && <Alert tone="red">{errorMessage(resend.error)}</Alert>}
            <Button variant="primary" className="w-full" loading={resend.isPending} onClick={() => resend.mutate()}>
              Send a new link
            </Button>
          </>
        )
      ) : (
        <Link to="/login" className="block">
          <Button variant="primary" className="w-full">
            Sign in to send a new link
          </Button>
        </Link>
      )}
    </AccountCard>
  );
}

/** /reset-password: asks for a reset link, or (with ?token=) sets the new password. */
export function ResetPasswordPage() {
  const [params] = useSearchParams();
  const token = params.get('token');
  const school = params.get('school') ?? undefined;
  return token ? <ChooseNewPassword token={token} school={school} /> : <RequestReset school={school} />;
}

function RequestReset({ school }: { school?: string }) {
  const [email, setEmail] = useState('');
  const request = useMutation({ mutationFn: () => api.post('/auth/password-reset/request', { email: email.trim(), school }) });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    request.mutate();
  };

  if (request.isSuccess) {
    return (
      <AccountCard icon={<MailCheck />} title="Check your email">
        <p>
          If an Edvora account uses <strong className="text-slate-900">{email.trim()}</strong>, we sent it a link to choose a new password. The link
          works for an hour.
        </p>
        <p>Nothing there after a few minutes? Check your spam folder, or ask your school’s technology team to reset your password.</p>
        <a href={signInHref(school)} className="block text-center font-medium text-brand-700 hover:underline">
          Back to sign in
        </a>
      </AccountCard>
    );
  }
  return (
    <AccountCard icon={<KeyRound />} title="Reset your password">
      <p>Enter the email you sign in with, and we’ll send you a link to choose a new password.</p>
      <form onSubmit={submit} className="space-y-4">
        {request.error && <Alert tone="red">{errorMessage(request.error)}</Alert>}
        <Field label="Email">
          <Input id="reset-email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)} />
        </Field>
        <Button type="submit" variant="primary" className="w-full" loading={request.isPending} disabled={!email.trim()}>
          Send the link
        </Button>
      </form>
      <a href={signInHref(school)} className="block text-center font-medium text-brand-700 hover:underline">
        Back to sign in
      </a>
    </AccountCard>
  );
}

function ChooseNewPassword({ token, school }: { token: string; school?: string }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [mismatch, setMismatch] = useState('');
  const reset = useMutation({ mutationFn: () => api.post<LinkResult>('/auth/password-reset', { token, password, school }) });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (password.length < 10) return setMismatch('Use at least 10 characters');
    if (password !== confirm) return setMismatch('The passwords don’t match');
    setMismatch('');
    reset.mutate();
  };

  if (reset.isSuccess) {
    return (
      <AccountCard icon={<CircleCheck />} title="Your password is changed">
        <p>You’ve been signed out everywhere. Sign in with your new password.</p>
        <Button variant="primary" className="w-full" onClick={() => window.location.assign(signInHref(reset.data.school))}>
          Sign in
        </Button>
      </AccountCard>
    );
  }
  return (
    <AccountCard icon={<KeyRound />} title="Choose a new password">
      <form onSubmit={submit} className="space-y-4">
        {reset.error && (
          <Alert tone="red">
            {errorMessage(reset.error)}{' '}
            <a href={`/reset-password${school ? `?school=${encodeURIComponent(school)}` : ''}`} className="font-medium underline">
              Send a new link
            </a>
          </Alert>
        )}
        <Field label="New password" hint="At least 10 characters" error={mismatch && password.length < 10 ? mismatch : undefined}>
          <Input id="reset-password" type="password" autoComplete="new-password" required value={password} onChange={(e) => setPassword(e.target.value)} />
        </Field>
        <Field label="Confirm new password" error={mismatch && password.length >= 10 ? mismatch : undefined}>
          <Input id="reset-confirm" type="password" autoComplete="new-password" required value={confirm} onChange={(e) => setConfirm(e.target.value)} />
        </Field>
        <Button type="submit" variant="primary" className="w-full" loading={reset.isPending}>
          Change password
        </Button>
      </form>
    </AccountCard>
  );
}
